import React from "react"
import ConventionTeamMember from "models/convention-api/v1/ConventionTeamMember"
import { Avatar, Box, Dialog, styled, Typography } from "@mui/material"
import { nameToInitials, pxToRem } from "utils/helpers"
import dompurify from "dompurify"
import IconButton from "@mui/material/IconButton"
import CloseIcon from "@mui/icons-material/Close"

interface TeamMemberCardDialogProps {
  teamMember: ConventionTeamMember
  onClose: () => void
}

const TeamMemberCardDialog = (props: TeamMemberCardDialogProps) => {
  const {
    teamMember,
    onClose
  } = props

  const name = getTeamMemberName(teamMember)

  return (
    <_Dialog open onClose={onClose}>
      <_CardContainer>
        <_CardHeader>
          <_CardHeaderAvatar
            src={teamMember.admin_avatar_thumb_url ?? undefined}
            children={nameToInitials(name)}
          />
          <_CardHeaderTextContainer>
            <Typography variant="header5">{name}</Typography>
            <Typography variant="paragraphRegular">{teamMember.admin_title}</Typography>
          </_CardHeaderTextContainer>
          <IconButton
            onClick={onClose}
            color="dark"
            aria-label={`Close ${name} dialog`}
          >
            <CloseIcon color="inherit"/>
          </IconButton>
        </_CardHeader>
        <_CardBioText
          component="p"
          variant="paragraphRegular"
          dangerouslySetInnerHTML={{
            __html: dompurify.sanitize(teamMember.admin_bio ?? ''),
          }} />
      </_CardContainer>
    </_Dialog>
  )
}

export default TeamMemberCardDialog

const _Dialog = styled(Dialog)(({ theme }) => ({
  "& > .MuiDialog-container > .MuiDialog-paper": {
    position: "relative",

    width: "100%",
    minWidth: pxToRem(300),
    maxWidth: pxToRem(500),
    maxHeight: pxToRem(700),

    borderRadius: pxToRem(10),

    backgroundColor: theme.palette.gray.lightest,
    boxShadow: `0 ${pxToRem(4)} ${pxToRem(4)} rgba(0, 0, 0, 0.25), 0 0 ${pxToRem(
      4,
    )} rgba(0, 0, 0, 0.12)`,

    overflowY: "hidden",
  },
})) as typeof Dialog

const _CardContainer = styled(Box)(({theme}) => ({
  display: 'flex',
  flexFlow: 'column nowrap',
  alignItems: 'flex-start',
  gap: pxToRem(15),
  width: '100%',
  height: '100%',
  minWidth: pxToRem(300),
  maxWidth: pxToRem(500),
  background: theme.palette.gray.lightest,
  overflow: 'hidden',
})) as typeof Box

const _CardHeader = styled(Box)(() => ({
  display: 'flex',
  flexFlow: 'row nowrap',
  gap: pxToRem(10),
  width: '100%',
  padding: `${pxToRem(15)} ${pxToRem(15)} 0 ${pxToRem(15)}`,
})) as typeof Box

const _CardHeaderAvatar = styled(Avatar)(({ theme }) => ({
  width: pxToRem(46),
  height: pxToRem(46),
  backgroundColor: theme.palette.primary.main,
})) as typeof Avatar

const _CardHeaderTextContainer = styled(Box)(() => ({
  flex: 1,
  display: 'flex',
  flexFlow: 'column nowrap',
  gap: pxToRem(5)
})) as typeof Box

const _CardBioText = styled(Typography, {})(({}) => ({
  flex: 1,
  width: '100%',
  height: '100%',
  padding: `0 ${pxToRem(15)} ${pxToRem(15)} ${pxToRem(15)}`,
  overflowY: 'auto',
})) as typeof Typography

const getTeamMemberName = (teamMember: ConventionTeamMember) => (
  teamMember.admin_first_name + ' ' + teamMember.admin_last_name
)
